import type { TClientePessoaFisicaPropostaSchema, TCompradorPropostaSchema } from '@/features/proposta/schemas'
import type { CompradorPessoaFisicaStepSchemaInput } from '@/features/proposta/schemas/compradores/comprador-pf.schema'
import { tipoDeCliente } from '@/types/cliente'

const gerarClientePrincipal = (comprador: CompradorPessoaFisicaStepSchemaInput): TClientePessoaFisicaPropostaSchema => {
  const isMenor = comprador.dados.maiorDe18Anos === 'nao'
  const maePai = comprador.maePai
  const email = () => {
    if (isMenor) {
      if (comprador.dados.email) return comprador.dados.email

      return maePai!.contato.email!
    }
    return comprador.contato!.email!
  }

  return {
    id: comprador.dados.id!,
    cpf: comprador.dados.cpf.replaceAll(/\D/g, ''),
    nome: comprador.dados.nome,
    nacionalidadeId: comprador.dados.nacionalidadeId,
    profissaoId: comprador.dados.profissaoId,
    email: email(),
    telefone: isMenor
      ? {
          numero: maePai!.contato.telefone!.numero.replaceAll(/\D/g, ''),
          origem: maePai!.contato.telefone!.origem!,
          principal: maePai!.contato.telefone!.principal!,
        }
      : {
          numero: comprador.contato!.telefone!.numero.replaceAll(/\D/g, ''),
          origem: comprador.contato!.telefone!.origem!,
          principal: comprador.contato!.telefone!.principal!,
        },
    imagem: comprador.imagem ?? null,
    maiorDe18Anos: comprador.dados.maiorDe18Anos === 'sim',
    possuiUniaoEstavel: isMenor ? null : comprador.estadoCivil?.uniaoEstavel === 'sim',
    regimeCasamento: comprador.estadoCivil?.regimeCasamento ?? null,
    estadoCivil: isMenor ? 'CivilSolteiro' : comprador.estadoCivil?.status,
    tipoDoCliente: comprador.tipoDoCliente,
  }
}

export const gerarClienteConjuge = (
  conjuge: CompradorPessoaFisicaStepSchemaInput['conjuge'],
  estadoCivil: CompradorPessoaFisicaStepSchemaInput['estadoCivil'],
): TClientePessoaFisicaPropostaSchema | null => {
  if (!conjuge) return null

  return {
    id: conjuge.dados.id!,
    cpf: conjuge.dados.cpf.replaceAll(/\D/g, ''),
    nome: conjuge.dados.nome,
    nacionalidadeId: conjuge.dados.nacionalidadeId,
    profissaoId: conjuge.dados.profissaoId,
    email: conjuge.contato.email!,
    telefone: {
      numero: conjuge.contato.telefone!.numero.replaceAll(/\D/g, ''),
      origem: conjuge.contato.telefone!.origem!,
      principal: true,
    },
    imagem: conjuge.imagem ?? null,
    maiorDe18Anos: true,
    possuiUniaoEstavel: estadoCivil?.uniaoEstavel ? estadoCivil.uniaoEstavel === 'sim' : null,
    estadoCivil: estadoCivil?.status ?? null,
    regimeCasamento: estadoCivil?.regimeCasamento ?? null,
    tipoDoCliente: 'Contato',
  }
}

export const gerarClienteMaePai = (
  maePai: CompradorPessoaFisicaStepSchemaInput['maePai'],
): TClientePessoaFisicaPropostaSchema | null => {
  if (!maePai) return null
  return {
    id: maePai.dados.id!,
    cpf: maePai.dados.cpf.replaceAll(/\D/g, ''),
    nome: maePai.dados.nome,
    nacionalidadeId: maePai.dados.nacionalidadeId,
    profissaoId: maePai.dados.profissaoId,
    email: maePai.contato.email!,
    telefone: {
      numero: maePai.contato.telefone!.numero.replaceAll(/\D/g, ''),
      origem: maePai.contato.telefone!.origem!,
      principal: true,
    },
    imagem: maePai?.imagem ?? null,
    maiorDe18Anos: true,
    possuiUniaoEstavel: maePai.estadoCivil?.uniaoEstavel ? maePai.estadoCivil.uniaoEstavel === 'sim' : null,
    estadoCivil: maePai.estadoCivil?.status,
    regimeCasamento: maePai.estadoCivil?.regimeCasamento ?? null,
    tipoDoCliente: 'Contato',
  }
}

export const gerarNovoVinculo = (
  comprador: CompradorPessoaFisicaStepSchemaInput,
): TClientePessoaFisicaPropostaSchema & { tipo: 'MaePai' | 'Conjuge' } => {
  const maePai = comprador.maePai
  const conjuge = comprador.conjuge

  const maePaiMapeado = maePai
    ? {
        id: maePai.dados.id!,
        cpf: maePai.dados.cpf.replaceAll(/\D/g, ''),
        nome: maePai.dados.nome,
        nacionalidadeId: maePai.dados.nacionalidadeId,
        profissaoId: maePai.dados.profissaoId,
        email: maePai.contato.email!,
        telefone: {
          numero: maePai.contato.telefone!.numero.replaceAll(/\D/g, ''),
          origem: maePai.contato.telefone!.origem!,
          principal: true,
        },
        imagem: maePai?.imagem ?? null,
        maiorDe18Anos: true,
        possuiUniaoEstavel: maePai.estadoCivil?.uniaoEstavel ? maePai.estadoCivil.uniaoEstavel === 'sim' : null,
        estadoCivil: maePai.estadoCivil?.status,
        regimeCasamento: maePai.estadoCivil?.regimeCasamento ?? null,
        tipoDoCliente: tipoDeCliente.Contato,
      }
    : null

  const conjugeMapeado = conjuge
    ? {
        id: conjuge.dados.id!,
        cpf: conjuge.dados.cpf.replaceAll(/\D/g, ''),
        nome: conjuge.dados.nome,
        nacionalidadeId: conjuge.dados.nacionalidadeId,
        profissaoId: conjuge.dados.profissaoId,
        email: conjuge.contato.email!,
        telefone: {
          numero: conjuge.contato.telefone!.numero.replaceAll(/\D/g, ''),
          origem: conjuge.contato.telefone!.origem!,
          principal: true,
        },
        imagem: conjuge.imagem ?? null,
        maiorDe18Anos: true,
        possuiUniaoEstavel: comprador.estadoCivil?.uniaoEstavel ? comprador.estadoCivil.uniaoEstavel === 'sim' : null,
        estadoCivil: comprador.estadoCivil?.status ?? null,
        regimeCasamento: comprador.estadoCivil?.regimeCasamento ?? null,
        tipoDoCliente: tipoDeCliente.Contato,
      }
    : null

  return maePai ? { ...maePaiMapeado!, tipo: 'MaePai' } : { ...conjugeMapeado!, tipo: 'Conjuge' }
}

export const gerarListaDeClientes = (
  comprador: CompradorPessoaFisicaStepSchemaInput,
): TClientePessoaFisicaPropostaSchema[] => {
  const clientePrincipal = gerarClientePrincipal(comprador)
  const maePaiMapeado = gerarClienteMaePai(comprador.maePai)
  const conjugeMapeado = gerarClienteConjuge(comprador.conjuge, comprador.estadoCivil)

  return [clientePrincipal, ...(maePaiMapeado ? [maePaiMapeado] : []), ...(conjugeMapeado ? [conjugeMapeado] : [])]
}

export const tranformarCompradorFormParaCompradorProposta = (
  comprador: CompradorPessoaFisicaStepSchemaInput,
): TCompradorPropostaSchema => {
  return {
    clienteId: comprador.dados.id!,
    tipo: 'PessoaFisica',
    tipoAssinatura: comprador.estadoCivil?.tipoAssinatura ?? null,
    vinculos: [
      ...(comprador.maePai
        ? [
            {
              clienteId: comprador.maePai.dados.id!,
              tipo: 'MaePai' as const,
            },
          ]
        : []),
      ...(comprador.conjuge
        ? [
            {
              clienteId: comprador.conjuge.dados.id!,
              tipo: 'Conjuge' as const,
            },
          ]
        : []),
    ],
  }
}
